import {Grid, MenuItem, Select, Typography, Box} from "@mui/material";
import React, {useState} from "react";
import {useInvestAssistantContext} from "../ContextType";
import {Slider} from "../Slider";
import {FooterButtons} from "../FooterButtons";

import {Label} from "../Label";
import {styled} from "@mui/material/styles";
import {haptic} from "../haptics";
import {fireEvent} from "../../Tracking/FireEvent";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";



const Boxstyle = styled(Box)(
    ({theme}) => `
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    row-gap: 16px;
    column-gap: 25px;
    border: 2px solid transparent;
    cursor: pointer;
    user-select: none;
    transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
    will-change: transform;
    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 10px 26px rgba(0, 0, 0, .10);
    }
    &:active {
        transform: translateY(-1px) scale(.995);
    }
    &:focus-visible {
        outline: none;
        border-color: #262527;
        box-shadow: 0 0 0 3px rgba(38, 37, 39, .20);
    }
    &:hover .weiter-cue .MuiSvgIcon-root {
        transform: translateX(3px);
    }
    /* Below 550px of the card's own width the button wraps underneath the text;
       at 550px and above it always stays to the right. */
    @container (max-width: 549.98px) {
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: 16px;
    }
    @media (prefers-reduced-motion: reduce) {
        transition: none;
        &:hover, &:active { transform: none; }
        &:hover .weiter-cue .MuiSvgIcon-root { transform: none; }
    }
`);

const BoxstyleHighlighted = styled(Boxstyle)(
    ({theme}) => `
    border-color: #fbe74c;
    &:hover {
        box-shadow: 0 10px 26px rgba(251, 231, 76, .28);
    }
`);

const IconBadge = styled(Box)(
    ({theme}) => `
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background-color: #f3f4f6;
    color: #262527;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform .18s ease, background-color .18s ease;
    & .MuiSvgIcon-root { font-size: 24px; }
`);

const IconBadgeHighlighted = styled(IconBadge)(
    ({theme}) => `
    background-color: #fbe74c;
`);

// Decorative "Weiter" affordance — the whole card is the actual button,
// so this must not capture clicks/focus of its own.
const WeiterCue = styled(Box)(
    ({theme}) => `
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 5px;
    margin-bottom: 5px;
    background-color: #262527;
    color: #ffffff;
    text-transform: uppercase;
    font-weight: bold;
    font-size: 14px;
    line-height: 1;
    padding: 10px 16px;
    border-radius: 4px;
    pointer-events: none;
    white-space: nowrap;
    & .weiter-cue__label {
        display: inline-block;
        line-height: 1;
    }
    & .MuiSvgIcon-root {
        display: block;
        font-size: 18px;
        transition: transform .18s ease;
    }
`);

export function Stage1() {
    const {equityCapitalState, setEquityCapitalState, monthlyInvestState, setMonthlyInvestState,
    type, source, setSource, setSelectedPath, handleNext} = useInvestAssistantContext();


    const isValid = () => {

        if (type === 'intern' && !source) {
            return false;
        }

        return equityCapitalState != undefined && monthlyInvestState != undefined;
    }

    // Selecting a path from a card: give tactile feedback, track the chosen action, set the path and advance.
    const choosePath = (path: string) => {
        haptic(12);
        fireEvent('invest-assistant-path-' + path);
        setSelectedPath(path);
        handleNext(true);
    }

    // Props that turn a card into a single accessible, keyboard-operable button.
    const cardActionProps = (path: string, label: string) => ({
        role: 'button',
        tabIndex: 0,
        'aria-label': label,
        onClick: () => choosePath(path),
        onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                choosePath(path);
            }
        },
    });

    return (
        <Slider nr={1}>
            <Grid container rowGap={3} justifyContent="center">
                <Grid item xs={12} pt={1} sx={{containerType: 'inline-size'}}>
                    <BoxstyleHighlighted {...cardActionProps('normal', 'Eine Beratung anfragen')}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 auto', minWidth: 0}}>
                            <IconBadgeHighlighted>
                                <SupportAgentIcon />
                            </IconBadgeHighlighted>
                            <Typography sx={{margin: '0 !important', lineHeight: 1.4}}>Eine Beratung / Immobilie berechnen lassen</Typography>
                        </Box>
                        <WeiterCue className="weiter-cue">
                            <span className="weiter-cue__label">Weiter</span>
                            <ArrowForwardIcon />
                        </WeiterCue>
                    </BoxstyleHighlighted>
                </Grid>
                <Grid item xs={12} pt={1} sx={{containerType: 'inline-size'}}>
                    <Boxstyle {...cardActionProps('rueckruf', 'Einen Rückruf anfordern')}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 auto', minWidth: 0}}>
                            <IconBadge>
                                <PhoneInTalkIcon />
                            </IconBadge>
                            <Typography sx={{margin: '0 !important', lineHeight: 1.4}}>Einen Rückruf</Typography>
                        </Box>
                        <WeiterCue className="weiter-cue">
                            <span className="weiter-cue__label">Weiter</span>
                            <ArrowForwardIcon />
                        </WeiterCue>
                    </Boxstyle>
                </Grid>
                <Grid item xs={12} pt={1} sx={{containerType: 'inline-size'}}>
                    <Boxstyle {...cardActionProps('newsletter', 'Zum Newsletter eintragen')}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 auto', minWidth: 0}}>
                            <IconBadge>
                                <MarkEmailReadIcon />
                            </IconBadge>
                            <Typography sx={{margin: '0 !important', lineHeight: 1.4}}>Erst mal nur zum Newsletter eintragen um über Bauprojekte informiert zu werden</Typography>
                        </Box>
                        <WeiterCue className="weiter-cue">
                            <span className="weiter-cue__label">Weiter</span>
                            <ArrowForwardIcon />
                        </WeiterCue>
                    </Boxstyle>
                </Grid>

                {type === 'intern' && <Grid item xs={12} pt={1}>
                    <Label>Leadquelle</Label>

                    <Select variant={"standard"}  value={source}
                            onChange={e => setSource(e.target.value)}>
                        <MenuItem value={'Instagram'}>Instagram</MenuItem>
                        <MenuItem value={'TikTok'}>TikTok</MenuItem>
                        <MenuItem value={'Facebook'}>Facebook</MenuItem>
                        <MenuItem value={'Google'}>Google</MenuItem>
                        <MenuItem value={'LinkedIn'}>LinkedIn</MenuItem>
                    </Select>
                    <Grid item xs={12}>
                        <FooterButtons forwardEnabled={isValid()} backVisible={false} increaseStepNumber={true}/>
                    </Grid>
                </Grid>
                }

            </Grid>
        </Slider>
    );
}